import { compact } from "lodash";
import { useCallback, useContext, useMemo, useState } from "react";
import {
  ComplianceProgramCalibrationType,
  PerformanceAssessmentAnswerFilter,
} from "types/graphql-schema";

import PerformanceAssessmentReportContext from "@apps/reporting/context/performance-assessment-report-context";
import { assertNonNull } from "@helpers/helpers";

import { isAssessmentReportAnswerType } from "./helpers";
import NineBoxMovePersonDialog, {
  NineBoxMove,
} from "./nine-box-move-person-dialog";
import { getNineBoxChartLabel } from "./nine-box/helpers";
import NineBoxCell from "./nine-box/nine-box-cell";
import NineBoxGrid, {
  SingleQuestionNineBoxGrid,
} from "./nine-box/nine-box-grid";
import {
  NineBoxAssessmentAnswer,
  NineBoxPerson,
  NineBoxQuestion,
} from "./nine-box/types";
import {
  getFilterByType,
  matchesPerformanceAssessmentFilter,
} from "./report-filters/helpers";
import { AssessmentReportFilterType } from "./report-filters/types";

// The questions whose rating a calibration can change.
const isCalibratableQuestion = (question: NineBoxQuestion) =>
  question.__typename === "RangeAssessmentQuestionNode" ||
  question.__typename === "TalentIndicatorAssessmentQuestionNode";

// A touch-only device, like a phone, has no drag.
const hasFinePointer = () =>
  typeof window.matchMedia !== "function" ||
  window.matchMedia("(any-pointer: fine)").matches;

/**
 * The nine-box grid of a review's answers. With `showPeople`, each box also
 * lists its people: only the Calibration tab asks for them. With
 * `allowMovingPeople` too, a viewer who can change a person's ratings drags
 * them to another box, and a dialog asks for the reason before it saves.
 */
const PerformanceAssessmentChart = ({
  onChangeAnswerFilterList,
  questions,
  showPeople = false,
  allowMovingPeople = false,
}: {
  questions: NineBoxQuestion[];
  showPeople?: boolean;
  allowMovingPeople?: boolean;
  onChangeAnswerFilterList: (
    answerFilterList: PerformanceAssessmentAnswerFilter[],
  ) => void;
}) => {
  const { userRows, chartLabels, viewOptions, complianceProgram } = useContext(
    PerformanceAssessmentReportContext,
  );
  // The move the dialog asks a reason for; null when it is closed.
  const [pendingMove, setPendingMove] = useState<NineBoxMove | null>(null);
  const [isDragSupported] = useState(hasFinePointer);
  const {
    userFilterList,
    reportsToFilterList,
    teamFilterList,
    enrollmentDateFilterList,
    roleFilterList,
    careerTrackFilterList,
    careerTrackLevelFilterList,
    talentIndicatorFilterList,
    answerFilterList,
    filterMatchType,
  } = useMemo(() => {
    const filterItems = viewOptions.filters.list;
    const userFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.USER,
    );
    const enrollmentDateFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.ENROLLMENT_DATE,
    );
    const reportsToFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.REPORTS_TO,
    );
    const answerFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.ANSWER,
    );
    const talentIndicatorFilterList = filterItems
      .filter(
        (item) => item.type === AssessmentReportFilterType.TALENT_INDICATOR,
      )
      .map((item) => item.data);
    const teamFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.TEAM,
    );
    const roleFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.ROLE,
    );
    const careerTrackFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.CAREER_TRACK,
    );
    const careerTrackLevelFilterList = getFilterByType(
      filterItems,
      AssessmentReportFilterType.CAREER_TRACK_LEVEL,
    );
    return {
      userFilterList,
      reportsToFilterList,
      teamFilterList,
      enrollmentDateFilterList,
      roleFilterList,
      careerTrackFilterList,
      careerTrackLevelFilterList,
      talentIndicatorFilterList,
      answerFilterList,
      filterMatchType: viewOptions.filters.type,
    };
  }, [viewOptions.filters]);

  const handleUpdateAnswerFilter = useCallback(
    (
      xFilter: PerformanceAssessmentAnswerFilter,
      yFilter?: PerformanceAssessmentAnswerFilter,
    ) => {
      onChangeAnswerFilterList(compact([xFilter, yFilter]));
    },
    [onChangeAnswerFilterList],
  );

  const xQuestion = useMemo(
    () =>
      questions.length > 0
        ? questions[viewOptions.nineBox.selectedXQuestionIndex]
        : null,
    [questions, viewOptions.nineBox.selectedXQuestionIndex],
  );
  const yQuestion = useMemo(
    () =>
      questions.length > 1 &&
      viewOptions.nineBox.selectedYQuestionIndex !== null
        ? questions[viewOptions.nineBox.selectedYQuestionIndex]
        : null,
    [questions, viewOptions.nineBox.selectedYQuestionIndex],
  );

  const filteredUserRows = useMemo(() => {
    return userRows.filter((reportRow) => {
      return matchesPerformanceAssessmentFilter({
        userFilterList,
        reportsToFilterList,
        teamFilterList,
        enrollmentDateFilterList,
        roleFilterList,
        careerTrackFilterList,
        careerTrackLevelFilterList,
        talentIndicatorFilterList,
        reportRow,
        matchType: filterMatchType,
      });
    });
  }, [
    userRows,
    filterMatchType,
    reportsToFilterList,
    userFilterList,
    teamFilterList,
    roleFilterList,
    careerTrackFilterList,
    careerTrackLevelFilterList,
    talentIndicatorFilterList,
    enrollmentDateFilterList,
  ]);

  const assessments = useMemo(
    () => compact(filteredUserRows.map((row) => row.assessment)),
    [filteredUserRows],
  );
  const personByAssessmentId = useMemo(
    () =>
      new Map<number, NineBoxPerson>(
        filteredUserRows.flatMap((row) =>
          row.assessment ? [[row.assessment.id, row.user]] : [],
        ),
      ),
    [filteredUserRows],
  );
  const peopleOf = (answers: NineBoxAssessmentAnswer[]) =>
    showPeople
      ? compact(
          answers.map((answer) =>
            personByAssessmentId.get(answer.assessmentId),
          ),
        )
      : undefined;

  const xQuestionAnswers = useMemo(
    () =>
      xQuestion && assessments.length > 0
        ? assessments.reduce((ansList, assessment) => {
            const answers = assessment.answers;
            return ansList.concat(
              compact(
                answers
                  .filter((a) => a?.questionId === xQuestion.id)
                  .map((a) =>
                    isAssessmentReportAnswerType(a)
                      ? {
                          assessmentId: assessment.id,
                          questionId: a.questionId,
                          integerAnswer: assertNonNull(a.integerAnswer),
                        }
                      : null,
                  ),
              ),
            );
          }, [] as NineBoxAssessmentAnswer[])
        : [],
    [xQuestion, assessments],
  );
  const yQuestionAnswers = useMemo(
    () =>
      yQuestion && assessments.length > 0
        ? assessments.reduce((ansList, assessment) => {
            const answers = assessment.answers;
            return ansList.concat(
              compact(
                answers
                  .filter((a) => a?.questionId === yQuestion.id)
                  .map((a) =>
                    isAssessmentReportAnswerType(a)
                      ? {
                          assessmentId: assessment.id,
                          questionId: a.questionId,
                          integerAnswer: assertNonNull(a.integerAnswer),
                        }
                      : null,
                  ),
              ),
            );
          }, [] as NineBoxAssessmentAnswer[])
        : [],
    [yQuestion, assessments],
  );

  const rowByUserId = useMemo(
    () => new Map(filteredUserRows.map((row) => [row.user.id, row])),
    [filteredUserRows],
  );
  const programHasCalibration =
    !!complianceProgram &&
    complianceProgram.calibration !== ComplianceProgramCalibrationType.Off;
  const axisQuestions = compact([xQuestion, yQuestion]);
  // With one question on both axes, a box gives two ratings for it: no drag.
  const canMovePeople =
    allowMovingPeople &&
    showPeople &&
    isDragSupported &&
    programHasCalibration &&
    axisQuestions.length > 0 &&
    xQuestion?.id !== yQuestion?.id &&
    axisQuestions.every(isCalibratableQuestion);
  // Only a viewer who can change the rating drags: one who can only suggest
  // a rating cannot. The server checks it again when the move saves.
  const canDragPerson = (person: NineBoxPerson) =>
    !!rowByUserId.get(person.id)?.canCalibrateAnswer.permission;
  // A drop on another box opens the dialog. The person stays in their box
  // until the move saves.
  const handleDropPerson = (personId: number, values: number[]) => {
    const row = rowByUserId.get(personId);
    if (!row?.assessment || !row.canCalibrateAnswer.permission) {
      return;
    }
    const answers = compact(row.assessment.answers);
    const changes = compact(
      axisQuestions.map((question, index) => {
        const answer = answers.find(
          (answer) => answer.questionId === question.id,
        );
        return answer && isAssessmentReportAnswerType(answer)
          ? {
              question,
              answerId: answer.id,
              from: assertNonNull(answer.integerAnswer),
              to: values[index],
            }
          : null;
      }),
    );
    if (
      changes.length !== axisQuestions.length ||
      changes.every(({ from, to }) => from === to)
    ) {
      return;
    }
    setPendingMove({
      person: row.user,
      targetId: row.assessment.target?.id ?? row.user.id,
      changes,
    });
  };
  const dragProps = (values: number[]) =>
    canMovePeople
      ? {
          canDragPerson,
          onDropPerson: (personId: number) =>
            handleDropPerson(personId, values),
        }
      : {};

  if (questions.length === 0 || userRows.length === 0) {
    return null;
  }

  return (
    <div className="p-6 rounded-lg mb-4 bg-gray-100">
      {pendingMove && (
        <NineBoxMovePersonDialog
          move={pendingMove}
          onClose={() => setPendingMove(null)}
        />
      )}
      {xQuestion && yQuestion && (
        <NineBoxGrid
          xQuestion={xQuestion}
          yQuestion={yQuestion}
          cellRenderer={({ xValue, yValue }) => {
            const xAnswers = xQuestionAnswers.filter(
              (answer) => answer.integerAnswer === xValue,
            );
            const yAnswerIds = yQuestionAnswers
              .filter((answer) => answer.integerAnswer === yValue)
              .map((answer) => answer.assessmentId);
            const matchingAnswers = xAnswers.filter((answer) =>
              yAnswerIds.includes(answer.assessmentId),
            );

            return (
              <NineBoxCell
                key={xValue}
                label={
                  getNineBoxChartLabel(
                    chartLabels,
                    xQuestion.id,
                    xValue,
                    yQuestion?.id,
                    yValue,
                  )?.label
                }
                isFiltered={
                  !!answerFilterList.find(
                    (answerFilter) =>
                      answerFilter.questionId === xQuestion.id &&
                      answerFilter.integerAnswer === xValue,
                  ) &&
                  !!answerFilterList.find(
                    (answerFilter) =>
                      answerFilter.questionId === yQuestion.id &&
                      answerFilter.integerAnswer === yValue,
                  )
                }
                count={matchingAnswers.length}
                totalAssessments={xQuestionAnswers.length}
                people={peopleOf(matchingAnswers)}
                {...dragProps([xValue, yValue])}
                onApplyFilter={() =>
                  handleUpdateAnswerFilter(
                    { questionId: xQuestion.id, integerAnswer: xValue },
                    { questionId: yQuestion.id, integerAnswer: yValue },
                  )
                }
                onRemoveFilter={() => onChangeAnswerFilterList([])}
              />
            );
          }}
        />
      )}
      {xQuestion && !yQuestion && (
        <SingleQuestionNineBoxGrid
          question={xQuestion}
          cellRenderer={({ value }) => {
            const matchingAnswers = xQuestionAnswers.filter(
              (answer) => answer.integerAnswer === value,
            );

            return (
              <NineBoxCell
                key={value}
                label={
                  getNineBoxChartLabel(chartLabels, xQuestion.id, value)?.label
                }
                isFiltered={
                  !!answerFilterList.find(
                    (answerFilter) =>
                      answerFilter.questionId === xQuestion.id &&
                      answerFilter.integerAnswer === value,
                  )
                }
                count={matchingAnswers.length}
                totalAssessments={xQuestionAnswers.length}
                people={peopleOf(matchingAnswers)}
                {...dragProps([value])}
                onApplyFilter={() =>
                  handleUpdateAnswerFilter({
                    questionId: xQuestion.id,
                    integerAnswer: value,
                  })
                }
                onRemoveFilter={() => onChangeAnswerFilterList([])}
              />
            );
          }}
        />
      )}
    </div>
  );
};

export default PerformanceAssessmentChart;
